import { IPublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../config/msalConfig";

const REFRESH_BUFFER_MS = 10 * 60 * 1000; // 10 minutes before expiry
const MIN_REFRESH_INTERVAL_MS = 60 * 1000; // Never refresh more often than once per minute

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let onTokenRefreshed: ((newToken: string) => void) | null = null;
let msalInstanceRef: IPublicClientApplication | null = null;

/**
 * Register a callback to be invoked when the token is refreshed.
 * The new token is passed directly to the callback to guarantee it's available.
 */
export function onTokenRefresh(callback: (newToken: string) => void): void {
  onTokenRefreshed = callback;
}

/**
 * Decodes the JWT payload to extract the expiration time.
 */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Silently acquires a new idToken and stores it in localStorage.
 * Returns the new token or null if refresh fails.
 */
async function refreshIdToken(
  msalInstance: IPublicClientApplication,
): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    console.warn("[TokenRefresh] No accounts found, cannot refresh token.");
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
      forceRefresh: true,
    });

    localStorage.setItem("idToken", response.idToken);

    if (onTokenRefreshed) {
      onTokenRefreshed(response.idToken);
    }

    return response.idToken;
  } catch (error) {
    console.error("[TokenRefresh] Silent token refresh failed:", error);
    return null;
  }
}

/**
 * Schedules a token refresh 10 minutes before the current token expires.
 * Stores the MSAL instance for use by forceTokenRefresh.
 */
export function scheduleTokenRefresh(
  msalInstance: IPublicClientApplication,
): void {
  msalInstanceRef = msalInstance;

  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const token = localStorage.getItem("idToken");
  if (!token) {
    console.warn(
      "[TokenRefresh] No idToken in localStorage to schedule refresh for.",
    );
    return;
  }

  const expiryMs = getTokenExpiry(token);
  if (!expiryMs) {
    console.warn("[TokenRefresh] Could not determine token expiry.");
    return;
  }

  const now = Date.now();
  const refreshAt = expiryMs - REFRESH_BUFFER_MS;
  const delay = Math.max(refreshAt - now, MIN_REFRESH_INTERVAL_MS);

  refreshTimer = setTimeout(() => {
    refreshIdToken(msalInstance).then((newToken) => {
      if (newToken) {
        scheduleTokenRefresh(msalInstance);
      }
    });
  }, delay);
}

/**
 * Immediately triggers a token refresh regardless of the scheduled timer.
 * Returns true if refresh was initiated, false if MSAL instance not available.
 * Used as a fallback when the token is found to be expired at send time.
 */
export function forceTokenRefresh(): boolean {
  if (!msalInstanceRef) {
    console.warn(
      "[TokenRefresh] Cannot force refresh — no MSAL instance available.",
    );
    return false;
  }
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  refreshIdToken(msalInstanceRef).then((newToken) => {
    if (newToken) {
      scheduleTokenRefresh(msalInstanceRef!);
    }
  });
  return true;
}

/**
 * Returns the current idToken if it is still valid (not expired).
 * Returns null if the token is missing or within 60 seconds of expiry.
 */
export function getValidToken(): string | null {
  const token = localStorage.getItem("idToken");
  if (!token) return null;
  const expiry = getTokenExpiry(token);
  if (!expiry) return null;
  return Date.now() < expiry - 60 * 1000 ? token : null;
}

/**
 * Clears the scheduled token refresh (e.g., on logout).
 */
export function clearTokenRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  onTokenRefreshed = null;
  msalInstanceRef = null;
}
